import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { UserInfo } from '@/views/userInfo/user-info-reference/types'
import type { BasicResult } from '../model/resultModel'
import type { BulkAPIResponse } from '@/types'
import type {
  UserBulkRegisterModel,
  UserBulkRegisterParameter,
  UserBulkRegisterResponse,
} from './model/user-info-model'
import type { ErrorModel } from '../model/errorModel'

import { bffHTTP } from '@/utils/http/axios'
import {
  handleAPIException,
  handleBulkAPIException,
  handleExceptionNormal,
} from '@/utils'
import { errorMessage } from '@/utils/util'
import { CharBoolean } from '@/enums'

// 利用者情報一覧api
export async function getData(params: any): Promise<any | undefined> {
  try {
    const response: AxiosResponse<any, any> = await bffHTTP({
      method: 'GET',
      url: '/users',
      params,
    })

    handleAPIException(response)
    return response
  } catch (error) {
    console.warn('getCarts', error)
  }
}

/**
 * @description 利用者情報（参照）データ取得API
 * @param userID  利用者ID
 * @returns  利用者情報
 */
export async function queryUserInfoReference(
  userID: string | number,
  corporate_number: string | number
): Promise<UserInfo | undefined> {
  try {
    const response: AxiosResponse<BasicResult<UserInfo>, any> = await bffHTTP({
      method: 'GET',
      url: `${corporate_number}/users`,
      params: { user_id: userID },
    })

    handleAPIException(response)

    return response.data.detail[0]
  } catch (error) {
    console.error('queryUserInfoReference', error)
  }
}

/**
 * @descripiton 利用者詳細情報削除 API
 * @param userID 削除対象利用者 ID
 * @param notFoundCallback データ見つからない場合のコールバック
 * @returns 削除成功か否か
 */
export async function deleteUserDetail(
  userID: number | string,
  notFoundCallback: Function = () => {}
): Promise<boolean> {
  try {
    const response: AxiosResponse<string | ErrorModel, null> = await bffHTTP({
      method: 'DELETE',
      url: `/users/${userID}`,
    })

    const failedResult = response.data as ErrorModel

    if (failedResult.error_code === 'EB0019') {
      notFoundCallback()
    }

    if (response.status !== 200) {
      throw failedResult
    }

    return response.status === 200
  } catch (error) {
    handleExceptionNormal(error)
    return false
  }
}

/**
 * @descripiton 利用者詳細情報更新 API
 * @param userInfo 対象利用者情報
 * @param notFoundCallback データ見つからない場合のコールバック
 * @returns 更新成功か否か
 */
export async function updateUserDetail(
  userInfo: UserInfo,
  notFoundCallback: Function = () => {}
): Promise<boolean> {
  try {
    const response: AxiosResponse<string | ErrorModel, null> = await bffHTTP({
      method: 'PUT',
      url: `/users/${userInfo.user_id}`,
      data: userInfo,
    })

    const failedResult = response.data as ErrorModel

    if (failedResult.error_code === 'EB0019') {
      notFoundCallback()
    }

    if (response.status !== 200) {
      throw failedResult
    }

    return response.status === 200
  } catch (error) {
    handleExceptionNormal(error)
    return false
  }
}

/**
 * @descripiton 利用者新規登録 API
 * @param userInfo 対象利用者情報
 * @returns 登録成功か否か
 */
export async function addUser(userInfo: UserInfo): Promise<boolean> {
  try {
    const response: AxiosResponse<BulkAPIResponse<UserBulkRegisterModel>> = await bffHTTP(
      <AxiosRequestConfig<{ user_list: UserBulkRegisterParameter }>>{
        method: 'POST',
        url: `/users/multi`,
        data: {
          user_list: [
            {
              family_name: userInfo.family_name,
              given_name: userInfo.given_name,
              email: userInfo.email,
              department: userInfo.department,
              employee_code: userInfo.employee_code,
              role_code_name: userInfo.role_name,
              chem_value_usage_flag_name:
                userInfo.chem_value_usage_flag === CharBoolean.TRUE ? '可' : '不可',
            },
          ],
        },
      }
    )

    handleAPIException(response)

    if (response.data.ng_count) {
      for (const element of response.data.detail[0].error_message as string[]) {
        setTimeout((): void => errorMessage(element, 'error'), 100)
      }

      return false
    }

    return response.status === 200
  } catch (error) {
    console.warn('addUser', error)
    return false
  }
}

/**
 * @descripiton 利用者一括登録 API
 * @param list 利用者情報一覧
 * @param businessExceptionCallback 業務エラーコールバック
 * @returns 登録成功か否か
 */
export async function addMultiUser(
  list: UserBulkRegisterParameter,
  businessExceptionCallback: (response: UserBulkRegisterResponse) => void
): Promise<boolean> {
  try {
    const mixedResponse = await sendUserBulkRegisterAPIManyTimes(list)
    const [_, succeed] = handleBulkAPIException(mixedResponse, businessExceptionCallback)

    return succeed
  } catch (error) {
    console.warn('addMultiUser', error)
    return false
  }
}

/**
 * @description 登録する利用者リストを分割して送信
 * @param list 登録する利用者リスト
 * @returns 纏めた応答
 */
async function sendUserBulkRegisterAPIManyTimes(
  list: UserBulkRegisterParameter
): Promise<UserBulkRegisterResponse> {
  /** @descriptipn 一回の送信件数 */
  const ONE_TIME_LENGTH = 1000

  let listForRegister: UserBulkRegisterParameter = [...list]

  const mixedResponse = <UserBulkRegisterResponse>{
    status: 200,
    data: {
      ok_count: 0,
      ng_count: 0,
      detail: <UserBulkRegisterModel>[],
    },
  }

  // 送信件数が多い時は送信回数を増やす
  while (listForRegister.length) {
    const currentRequestList: UserBulkRegisterParameter = listForRegister.filter(
      (_, index: number): boolean => index < ONE_TIME_LENGTH
    )

    const response: UserBulkRegisterResponse = await bffHTTP(<
      AxiosRequestConfig<{ user_list: UserBulkRegisterParameter }>
    >{
      method: 'POST',
      url: `/users/multi`,
      data: { user_list: currentRequestList },
      params: { holdLoading: listForRegister.length > ONE_TIME_LENGTH },
    })

    // 一括登録の業務エラー以外の例外が発生した場合は後続の登録を中断する
    if (response.status !== 200) {
      mixedResponse.status = response.status
      break
    }

    // 登録済みのリスト部分をカット
    listForRegister = listForRegister.filter(
      (_, index: number): boolean => index >= ONE_TIME_LENGTH
    )

    mixedResponse.data.detail = [...mixedResponse.data.detail, ...response.data.detail]
    mixedResponse.data.ng_count += response.data.ng_count
    mixedResponse.data.ok_count += response.data.ok_count
  }

  return mixedResponse
}

/**
 * @descripiton 利用者詳細情報更新 API(ゲスト用)
 * @param userInfo 対象利用者情報
 * @param notFoundCallback データ見つからない場合のコールバック
 * @returns 更新成功か否か
 */
export async function updateUserDetailGuest(
  userInfo: UserInfo,
  notFoundCallback: Function = () => {}
): Promise<boolean> {
  try {
    const response: AxiosResponse<string | ErrorModel, null> = await bffHTTP({
      method: 'PUT',
      url: `/auth/users/direct/${userInfo.user_id}`,
      data: userInfo,
    })

    const failedResult = response.data as ErrorModel

    if (failedResult.error_code === 'EB0019') {
      notFoundCallback()
    }

    if (response.status !== 200) {
      throw failedResult
    }

    return response.status === 200
  } catch (error) {
    handleExceptionNormal(error)
    return false
  }
}
